'use client';

import Link from 'next/link';

import { useTranslations } from '@/components/language-provider';
import { JsonLd } from '@/components/seo/json-ld';
import { buildBreadcrumbJsonLd } from '@/lib/seo';

const SITE_URL = 'https://whoplays.us';
const PAGE_PATH = '/pickleball-app';
const PAGE_URL = `${SITE_URL}${PAGE_PATH}`;
const APP_STORE_URL = 'https://apps.apple.com/us/app/whoplays-us-self-pickleball/id6451442623?platform=iphone';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.whosplays.app';

export function PickleballAppPage() {
  const { messages, locale } = useTranslations();
  const content = messages.pickleballAppPage;

  const breadcrumbsJsonLd = buildBreadcrumbJsonLd([
    { name: 'Home', path: '/' },
    { name: content.seo.breadcrumbName, path: PAGE_PATH },
  ]);

  const softwareApplicationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'WhoPlays',
    applicationCategory: 'SportsApplication',
    operatingSystem: 'iOS, Android, Web',
    inLanguage: locale,
    url: PAGE_URL,
    description: content.seo.description,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    publisher: {
      '@type': 'Organization',
      name: 'WhoPlays US',
      url: SITE_URL,
    },
    featureList: content.jsonLd.featureList,
    downloadUrl: [APP_STORE_URL, PLAY_STORE_URL],
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: content.faq.items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <JsonLd data={breadcrumbsJsonLd} />
      <JsonLd data={softwareApplicationJsonLd} />
      <JsonLd data={faqJsonLd} />

      <main className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-12 md:px-6">
        <section className="rounded-3xl border border-border/60 bg-surface/90 p-7 shadow-lg shadow-primary/10 md:p-10">
          <div className="space-y-5">
            <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-muted/40 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <span className="size-2 rounded-full bg-primary" aria-hidden="true" />
              {content.badge}
            </span>
            <h1 className="text-balance text-4xl font-bold tracking-tight text-foreground sm:text-5xl">{content.title}</h1>
            <p className="max-w-3xl text-base text-muted-foreground sm:text-lg">{content.description}</p>
            <div className="flex flex-wrap gap-3">
              <Link
                href="/venues/locations"
                className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold btn-primary shadow-md shadow-primary/30 transition hover:-translate-y-0.5 hover:bg-primary/90"
              >
                {content.ctaBrowse}
              </Link>
              <Link
                href="/app-stores"
                className="inline-flex items-center justify-center rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition hover:-translate-y-0.5 hover:shadow"
              >
                {content.ctaDownload}
              </Link>
            </div>
          </div>
        </section>

        <section className="grid gap-5 md:grid-cols-3">
          {content.cards.map((card) => (
            <article key={card.title} className="rounded-2xl border border-border/60 bg-surface p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-foreground">{card.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{card.description}</p>
            </article>
          ))}
        </section>

        <section className="grid gap-6 rounded-3xl border border-border/60 bg-surface p-7 shadow-md md:grid-cols-2 md:p-8">
          <article className="space-y-3">
            <h2 className="text-2xl font-semibold text-foreground">{content.players.title}</h2>
            <ul className="space-y-1 text-sm text-muted-foreground">
              {content.players.points.map((point) => (
                <li key={point}>- {point}</li>
              ))}
            </ul>
            <Link
              href="/auth/signup"
              className="inline-flex items-center rounded-full border border-border px-4 py-2 text-sm font-semibold text-foreground transition hover:-translate-y-0.5 hover:shadow"
            >
              {content.players.cta}
            </Link>
          </article>

          <article className="space-y-3">
            <h2 className="text-2xl font-semibold text-foreground">{content.operators.title}</h2>
            <ul className="space-y-1 text-sm text-muted-foreground">
              {content.operators.points.map((point) => (
                <li key={point}>- {point}</li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2">
              <Link
                href="/court-intelligence"
                className="inline-flex items-center rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:-translate-y-0.5 hover:bg-primary/90"
              >
                {content.operators.primaryCta}
              </Link>
              <Link
                href="/court-intelligence/get-started"
                className="inline-flex items-center rounded-full border border-border px-4 py-2 text-sm font-semibold text-foreground transition hover:-translate-y-0.5 hover:shadow"
              >
                {content.operators.secondaryCta}
              </Link>
            </div>
          </article>
        </section>

        <section className="rounded-3xl border border-border/60 bg-surface p-7 shadow-md md:p-8">
          <h2 className="text-2xl font-semibold text-foreground">{content.faq.title}</h2>
          <div className="mt-4 space-y-3">
            {content.faq.items.map((item) => (
              <details key={item.question} className="rounded-xl border border-border/60 bg-background/70 p-4">
                <summary className="cursor-pointer text-sm font-semibold text-foreground">{item.question}</summary>
                <p className="mt-2 text-sm text-muted-foreground">{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </main>
    </>
  );
}
